import { API_BASE, api } from "@/lib/apiClient";
import type { Order } from "@/lib/projects";
import type {
  HeroUsageRowPayload,
  KundeTimesheetSettings,
  TimesheetDetailBundle,
  TimesheetMaterialRecord,
  TimesheetRowRecord,
  TimesheetUpsertPayload,
} from "@/features/timesheetFlow/timesheetFlowTypes";
import { normalizeBillingQuestionsFromApi } from "@/features/timesheetFlow/billingQuestionsNormalize";
import { normalizeMaterialForApi } from "@/features/timesheetFlow/lib/materialSourceType";

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
}

export async function fetchBlob(path: string): Promise<Blob> {
  const res = await fetch(buildUrl(path), { credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export interface ProjectSearchHit {
  id: number;
  auftragsnummer?: string | null;
  beschreibung?: string | null;
  kunde?: string | null;
  ort?: string | null;
  ort_zusatz?: string | null;
  ordnernummer?: string | null;
  jahr?: number | null;
  hero_project_id?: number | null;
  kunden_id?: number | null;
  ordner_id?: number | null;
  sordner_id?: number | null;
  auftragsart?: string | null;
  hero_custom_fields?: Order["hero_custom_fields"];
  project_completed?: boolean | null;
}

interface RawProjectSearchRow {
  id: number;
  auftragsnummer?: string | null;
  beschreibung?: string | null;
  kunde?: string | null;
  ort?: string | null;
  ort_zusatz?: string | null;
  ordnernummer?: string | null;
  jahr?: number | null;
  hero_project_id?: number | null;
  kunden_id?: number | null;
  ordner_id?: number | null;
  sordner_id?: number | null;
  hero_custom_fields?: Order["hero_custom_fields"];
  project_completed?: boolean | null;
}

const TIMESHEET_PROJECT_LIST_PARAMS = {
  limit: "25",
  status: "offen",
  include_timesheets: "false",
  include_timesheet_numbers: "false",
  include_tags: "false",
  include_hero_custom_fields: "true",
} as const;

function mapRawProjectSearchRow(row: RawProjectSearchRow): ProjectSearchHit {
  const jahrRaw = row.jahr;
  const jahr =
    typeof jahrRaw === "number" && Number.isFinite(jahrRaw) ? jahrRaw : null;
  return {
    id: Number(row.id),
    auftragsnummer: row.auftragsnummer ?? null,
    beschreibung: row.beschreibung ?? null,
    kunde: row.kunde ?? null,
    ort: row.ort ?? null,
    ort_zusatz: row.ort_zusatz ?? null,
    ordnernummer: row.ordnernummer ?? null,
    jahr,
    hero_project_id: row.hero_project_id ?? null,
    kunden_id: row.kunden_id ?? null,
    ordner_id: row.ordner_id ?? null,
    sordner_id: row.sordner_id ?? null,
    hero_custom_fields: row.hero_custom_fields ?? undefined,
    project_completed: row.project_completed ?? null,
  };
}

function mapProjectSearchRows(rows: RawProjectSearchRow[] | null | undefined): ProjectSearchHit[] {
  return (rows || [])
    .filter((row) => row && row.id != null)
    .map(mapRawProjectSearchRow);
}

/**
 * Volltextsuche wie Projekte-/Material-Listen (`GET /auftraege?q=…`), nur offene Aufträge.
 */
export async function searchProjects(q: string): Promise<ProjectSearchHit[]> {
  const query = q.trim();
  if (!query) return [];
  const params = new URLSearchParams(TIMESHEET_PROJECT_LIST_PARAMS);
  params.set("q", query);
  const data = await api.get<RawProjectSearchRow[]>(`/auftraege?${params.toString()}`);
  return mapProjectSearchRows(data);
}

export async function listRecentProjects(): Promise<ProjectSearchHit[]> {
  const params = new URLSearchParams();
  params.set("limit", "25");
  params.set("include_completed", "false");
  const data = await api.get<RawProjectSearchRow[]>(
    `/me/recent-auftraege?${params.toString()}`,
  );
  return mapProjectSearchRows(data);
}

export async function listLatestProjects(): Promise<ProjectSearchHit[]> {
  const params = new URLSearchParams(TIMESHEET_PROJECT_LIST_PARAMS);
  params.set("limit", "20");
  const data = await api.get<RawProjectSearchRow[]>(`/auftraege/latest?${params.toString()}`);
  return mapProjectSearchRows(data);
}

/** Leeres Feld → zuletzt benutzte + neueste offene Aufträge; mit Eingabe → Volltextsuche. */
export async function loadTimesheetProjectOptions(q: string): Promise<ProjectSearchHit[]> {
  const trimmed = q.trim();
  if (!trimmed) {
    const [recent, latest] = await Promise.all([listRecentProjects(), listLatestProjects()]);
    const seen = new Set<number>();
    const merged: ProjectSearchHit[] = [];
    for (const item of [...recent, ...latest]) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      merged.push(item);
      if (merged.length >= 25) break;
    }
    return merged;
  }
  return searchProjects(trimmed);
}

export async function getTimesheetDetail(timesheetId: number): Promise<TimesheetDetailBundle> {
  return api.get<TimesheetDetailBundle>(`/timesheets/${timesheetId}`);
}

export async function getOrderForTimesheet(auftragId: number): Promise<Order> {
  return api.get<Order>(`/auftraege/${auftragId}`);
}

export async function createTimesheet(
  payload: TimesheetUpsertPayload,
  rows: TimesheetRowRecord[],
  materials: TimesheetMaterialRecord[],
): Promise<{ id: number }> {
  return api.post<{ id: number }>("/timesheets", {
    payload,
    rows,
    materials: materials.map(normalizeMaterialForApi),
  });
}

export async function fetchTimesheetPreviewPdf(
  payload: TimesheetUpsertPayload,
  rows: TimesheetRowRecord[],
  materials: TimesheetMaterialRecord[],
): Promise<Blob> {
  const res = await fetch(buildUrl("/timesheets/preview-pdf"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      payload,
      rows,
      materials: materials.map(normalizeMaterialForApi),
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export async function updateTimesheet(
  timesheetId: number,
  payload: TimesheetUpsertPayload,
  rows: TimesheetRowRecord[],
  materials: TimesheetMaterialRecord[],
): Promise<unknown> {
  return api.put(`/timesheets/${timesheetId}`, {
    payload,
    rows,
    materials: materials.map(normalizeMaterialForApi),
  });
}

export async function postHeroUsage(
  timesheetId: number,
  rows: HeroUsageRowPayload[],
): Promise<void> {
  await api.post(`/timesheets/${timesheetId}/hero-usage`, rows);
}

let kundenSettingsCache: Map<number, KundeTimesheetSettings> | null = null;
let kundenSettingsLoadPromise: Promise<void> | null = null;

async function ensureKundenSettingsCache(): Promise<Map<number, KundeTimesheetSettings>> {
  if (kundenSettingsCache) return kundenSettingsCache;
  if (!kundenSettingsLoadPromise) {
    kundenSettingsLoadPromise = api
      .get<KundeTimesheetSettings[]>("/kunden")
      .then((list) => {
        kundenSettingsCache = new Map();
        for (const k of Array.isArray(list) ? list : []) {
          if (k.id != null) kundenSettingsCache.set(k.id, k);
        }
      })
      .catch(() => {
        kundenSettingsCache = new Map();
      });
  }
  await kundenSettingsLoadPromise;
  return kundenSettingsCache!;
}

/** Kundeneinstellungen für Stundennachweise (wie klassische Seite via Kundenliste). */
export async function getKundeSettings(kundenId: number): Promise<KundeTimesheetSettings> {
  const cache = await ensureKundenSettingsCache();
  return (
    cache.get(kundenId) ?? {
      id: kundenId,
      show_time_range_on_timesheet: true,
      use_travel_eighths: false,
    }
  );
}

export interface HeroTimeEntry {
  _uuid?: string;
  start?: string;
  duration_in_seconds?: number;
  comment?: string;
  category_name?: string;
  partner_name?: string;
  partner?: { id?: number | string; full_name?: string; name?: string; account_type?: string };
  partner_id?: number | string;
  partner_account_type?: string;
  uuid?: string;
  hero_uuid?: string;
  row_key?: string;
  [key: string]: unknown;
}

export async function getHeroTimes(heroProjectId: number): Promise<HeroTimeEntry[]> {
  const data = await api.get<HeroTimeEntry[] | { times?: HeroTimeEntry[] }>(
    `/auftraege/times/${heroProjectId}`,
  );
  if (Array.isArray(data)) return data;
  if (data && Array.isArray((data as { times?: HeroTimeEntry[] }).times)) {
    return (data as { times: HeroTimeEntry[] }).times;
  }
  return [];
}

export interface HeroUsageEntry {
  status?: string;
  used_timesheet_id?: number | null;
  used_seconds?: number | null;
  total_seconds?: number | null;
  timesheet_number?: number | null;
  display_number?: string | null;
}

export interface HeroUsageMap {
  [uuid: string]: HeroUsageEntry;
}

interface HeroUsageApiRow {
  hero_uuid?: string;
  used_timesheet_id?: number | null;
  used_seconds?: number | null;
  timesheet_number?: number | null;
  display_number?: string | null;
}

export async function getHeroUsage(auftragId: number): Promise<HeroUsageMap> {
  const data = await api.get<{ usage?: HeroUsageApiRow[]; used_uuids?: string[] }>(
    `/auftraege/${auftragId}/hero-usage`,
  );
  const map: HeroUsageMap = {};
  const rows = Array.isArray(data?.usage) ? data.usage : [];
  for (const entry of rows) {
    const uuid = entry.hero_uuid;
    if (!uuid) continue;
    map[uuid] = {
      used_timesheet_id: entry.used_timesheet_id ?? null,
      used_seconds: entry.used_seconds ?? null,
      timesheet_number: entry.timesheet_number ?? null,
      display_number: entry.display_number ?? null,
    };
  }
  return map;
}

export interface SubcontractorTimeEntry {
  id: number;
  work_date?: string | null;
  person_name?: string | null;
  user_login?: string | null;
  user_display?: string | null;
  hours_decimal?: number | null;
  comment?: string | null;
  used_timesheet_id?: number | null;
  timesheet_id?: number | null;
  [key: string]: unknown;
}

export async function getSubcontractorUsage(auftragId: number): Promise<Map<number, number | null>> {
  const data = await api.get<{
    usage?: Array<{ subcontractor_time_id?: number; timesheet_id?: number | null }>;
  }>(`/auftraege/${auftragId}/subcontractor-usage?include_all=true`);
  const map = new Map<number, number | null>();
  for (const entry of data?.usage ?? []) {
    const id = Number(entry.subcontractor_time_id);
    if (!Number.isFinite(id)) continue;
    map.set(id, entry.timesheet_id ?? null);
  }
  return map;
}

export async function getSubcontractorTimes(auftragId: number): Promise<SubcontractorTimeEntry[]> {
  const data = await api.get<SubcontractorTimeEntry[]>(
    `/auftraege/${auftragId}/subcontractor-times?include_all=true`,
  );
  return Array.isArray(data) ? data : [];
}

export interface AppTimeEntry {
  id: number;
  work_date?: string | null;
  user_login?: string | null;
  user_display?: string | null;
  hours_decimal?: number | null;
  time_from?: string | null;
  time_to?: string | null;
  entry_type?: string | null;
  comment?: string | null;
  used_timesheet_id?: number | null;
  timesheet_id?: number | null;
  claimed_hours_decimal?: number | null;
  claimed_hours?: number | null;
  open_hours?: number | null;
  [key: string]: unknown;
}

export async function getAppTimeUsage(auftragId: number): Promise<Map<number, number | null>> {
  const data = await api.get<{
    usage?: Array<{ employee_time_id?: number; timesheet_id?: number | null }>;
  }>(`/auftraege/${auftragId}/app-time-usage`);
  const map = new Map<number, number | null>();
  for (const entry of data?.usage ?? []) {
    const id = Number(entry.employee_time_id);
    if (!Number.isFinite(id)) continue;
    map.set(id, entry.timesheet_id ?? null);
  }
  return map;
}

export async function getAppTimes(auftragId: number): Promise<AppTimeEntry[]> {
  const data = await api.get<AppTimeEntry[]>(`/auftraege/${auftragId}/app-times`);
  return Array.isArray(data) ? data : [];
}

export interface OrderedMaterialSuggestion {
  value: string;
  label?: string;
  text?: string;
  menge?: number | null;
  default_menge?: number | null;
  einheit?: string | null;
  bestellnummer?: string | null;
  source_type?: string | null;
  source_type_db?: string | null;
  order_line_id?: number | null;
  bestellung_position_id?: number | null;
  ma_position_id?: number | null;
  gd_position_id?: number | null;
  ma_nummer?: string | null;
  artikel_id?: number | null;
  hero_nr?: string | null;
  artikelnummer?: string | null;
  image_src?: string | null;
  bemerkung?: string | null;
  quelle_typ?: string | null;
  quelle_label?: string | null;
  used_timesheet_id?: number | null;
  [key: string]: unknown;
}

export async function getOrderedMaterials(auftragId: number): Promise<OrderedMaterialSuggestion[]> {
  const data = await api.get<OrderedMaterialSuggestion[]>(
    `/auftraege/${auftragId}/ordered-materials?for_timesheet=1`,
  );
  return Array.isArray(data) ? data : [];
}

export async function getFestpreisMaterialSuggestions(
  auftragId: number,
): Promise<OrderedMaterialSuggestion[]> {
  const data = await api.get<OrderedMaterialSuggestion[]>(
    `/auftraege/${auftragId}/festpreis-material-suggestions`,
  );
  return Array.isArray(data) ? data : [];
}

export async function getKundeStandardMaterials(kundenId: number): Promise<OrderedMaterialSuggestion[]> {
  const data = await api.get<OrderedMaterialSuggestion[]>(
    `/kunden/${kundenId}/standardmaterialien`,
  );
  return Array.isArray(data) ? data : [];
}

export async function getOrdnerStandardMaterials(ordnerId: number): Promise<OrderedMaterialSuggestion[]> {
  const data = await api.get<OrderedMaterialSuggestion[]>(
    `/ordner/${ordnerId}/standardmaterialien`,
  );
  return Array.isArray(data) ? data : [];
}

export async function getSOrdnerStandardMaterials(sordnerId: number): Promise<OrderedMaterialSuggestion[]> {
  const data = await api.get<OrderedMaterialSuggestion[]>(
    `/sordner/${sordnerId}/standardmaterialien`,
  );
  return Array.isArray(data) ? data : [];
}

export interface MaterialSearchHit {
  artikel_id?: number | null;
  hero_nr?: string | null;
  text?: string | null;
  einheit?: string | null;
  [key: string]: unknown;
}

export async function searchMaterials(q: string): Promise<MaterialSearchHit[]> {
  const data = await api.get<MaterialSearchHit[]>(
    `/materials/suggestions?q=${encodeURIComponent(q)}&tokens_all=1`,
  );
  return Array.isArray(data) ? data : [];
}

export interface WorkDescriptionAssistResult {
  ai_enabled?: boolean;
  suggested_text?: string | null;
  needs_change?: boolean;
  reason?: string | null;
}

export async function workDescriptionAssist(text: string): Promise<WorkDescriptionAssistResult> {
  return api.post<WorkDescriptionAssistResult>("/timesheets/work-description-assist", { text });
}

export async function submitProjectCompletion(
  auftragId: number,
  statementText: string,
  imageDataUrl: string,
): Promise<{ sharepoint_web_url?: string | null }> {
  return api.post(`/auftraege/${auftragId}/project-completion`, {
    statement_text: statementText,
    image_data_url: imageDataUrl,
  });
}

export async function submitMuendlichBeauftragt(
  auftragId: number,
  payload: {
    statementText: string;
    imageDataUrl: string;
    notiz?: string | null;
  },
): Promise<{ beauftragt?: boolean; sharepoint_web_url?: string | null }> {
  return api.post(`/auftraege/${auftragId}/muendlich-beauftragt`, {
    statement_text: payload.statementText,
    image_data_url: payload.imageDataUrl,
    notiz: payload.notiz ?? null,
  });
}

export interface LieferscheinQuellePosition {
  id: number;
  position_nr?: number | null;
  sort_index?: number | null;
  position_typ: string;
  bezeichnung: string;
  beschreibung?: string | null;
  einheit?: string | null;
  menge: number;
  editierbar: boolean;
}

export interface LieferscheinHeroAngebot {
  hero_document_id: number;
  document_number?: string | null;
  document_name?: string | null;
}

export interface LieferscheinQuelle {
  available: boolean;
  auftrag_id: number;
  quelle_typ?: string | null;
  quelle_dokument_id?: number | null;
  quelle_dokument_nummer?: string | null;
  warnung_ohne_ab: boolean;
  warnung_text?: string | null;
  positionen: LieferscheinQuellePosition[];
  hero_angebot?: LieferscheinHeroAngebot | null;
}

export interface HeroAngebotPosition {
  position_typ: string;
  bezeichnung: string;
  beschreibung?: string | null;
  einheit?: string | null;
  menge: number;
  einzelpreis_netto: number;
  gesamt_netto?: number;
  steuersatz_prozent?: number;
  nr?: string | null;
  sort_index?: number;
}

export interface HeroAngebotPositionenResponse {
  available: boolean;
  auftrag_id: number;
  hero_document_id?: number | null;
  document_number?: string | null;
  document_value?: number | null;
  source?: string | null;
  quantities_incomplete: boolean;
  positionen: HeroAngebotPosition[];
}

export interface LieferscheinCreateResult {
  dokument_id: number;
  dokument_nummer?: string | null;
  status: string;
  project_completed: boolean;
  has_rueckstand: boolean;
}

export interface AuftragLieferscheinSummary {
  id: number;
  dokument_nummer?: string | null;
  status?: string | null;
  betreff?: string | null;
  created_at?: string | null;
  has_open_rueckstand?: boolean;
}

export async function fetchAuftragLieferscheine(
  auftragId: number,
): Promise<AuftragLieferscheinSummary[]> {
  const data = await api.get<AuftragLieferscheinSummary[]>(
    `/auftraege/${auftragId}/lieferscheine`,
  );
  return Array.isArray(data) ? data : [];
}

export async function fetchLieferscheinQuelle(auftragId: number): Promise<LieferscheinQuelle> {
  return api.get<LieferscheinQuelle>(`/auftraege/${auftragId}/lieferschein-quelle`);
}

export async function fetchHeroAngebotPositionen(
  auftragId: number,
): Promise<HeroAngebotPositionenResponse> {
  return api.get<HeroAngebotPositionenResponse>(
    `/auftraege/${auftragId}/hero-angebot-positionen`,
  );
}

export type LieferscheinCreateBody = {
  positionen?: Array<{
    quelle_position_id: number;
    geliefert: number;
    rueckstand_entfernt?: boolean;
  }>;
  manuelle_positionen?: Array<{
    bezeichnung: string;
    beschreibung?: string | null;
    einheit?: string | null;
    position_typ?: string;
    beauftragt: number;
    geliefert: number;
    rueckstand_entfernt?: boolean;
    einzelpreis_netto?: number;
    steuersatz_prozent?: number;
  }>;
  lieferdatum?: string | null;
};

export async function createFestpreisLieferschein(
  auftragId: number,
  body: LieferscheinCreateBody,
): Promise<LieferscheinCreateResult> {
  return api.post<LieferscheinCreateResult>(`/auftraege/${auftragId}/lieferschein`, body);
}

export async function fetchLieferscheinPreviewPdf(
  auftragId: number,
  body: LieferscheinCreateBody,
): Promise<Blob> {
  const res = await fetch(buildUrl(`/auftraege/${auftragId}/lieferschein/preview-pdf`), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export interface LieferscheinActionsInfo {
  dokument_id: number;
  auftrag_id: number;
  dokument_nummer?: string | null;
  status: string;
  betreff?: string | null;
  kunde_name?: string | null;
  ansprechpartner_name?: string | null;
  recipients: string[];
  cc?: string[];
  created_at?: string | null;
  created_by_name?: string | null;
  versendet_am?: string | null;
  versendet_an?: string | null;
  lieferdatum?: string | null;
}

export async function fetchLieferscheinActions(
  auftragId: number,
  docId: number,
): Promise<LieferscheinActionsInfo> {
  return api.get<LieferscheinActionsInfo>(
    `/auftraege/${auftragId}/lieferschein/${docId}/actions`,
  );
}

export function lieferscheinPdfApiPath(auftragId: number, docId: number): string {
  return `/auftraege/${auftragId}/lieferschein/${docId}/pdf`;
}

export function lieferscheinPdfUrl(auftragId: number, docId: number): string {
  return buildUrl(lieferscheinPdfApiPath(auftragId, docId));
}

export async function getLieferscheinPrintUrl(
  auftragId: number,
  docId: number,
): Promise<{ url: string; expires_at?: number }> {
  return api.get(`/auftraege/${auftragId}/lieferschein/${docId}/pdf/print-url`);
}

export async function sendLieferscheinMail(
  auftragId: number,
  docId: number,
  payload: { to: string; cc?: string; subject?: string; mark_versendet?: boolean },
): Promise<{ ok: boolean; recipients: string[] }> {
  return api.post(`/auftraege/${auftragId}/lieferschein/${docId}/send-mail`, payload);
}

export async function closeProject(timesheetId: number): Promise<void> {
  await api.post(`/timesheets/${timesheetId}/close-project`, {});
}

export function pdfGenerateUrl(timesheetId: number): string {
  return buildUrl(`/timesheets/${timesheetId}/pdf/generate`);
}

export function pdfLatestUrl(timesheetId: number): string {
  return buildUrl(`/timesheets/${timesheetId}/pdf/latest`);
}

export interface TimesheetScanAttachment {
  id: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  created_at?: string | null;
}

export async function listTimesheetScanAttachments(
  timesheetId: number,
): Promise<TimesheetScanAttachment[]> {
  const data = await api.get<TimesheetScanAttachment[] | { items?: TimesheetScanAttachment[] }>(
    `/timesheets/${timesheetId}/attachments`,
  );
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.items) ? data.items : [];
}

export async function uploadTimesheetPdf(timesheetId: number): Promise<void> {
  await api.post(`/timesheets/${timesheetId}/pdf/upload`, {});
}

export async function signCompany(timesheetId: number, imageDataUrl: string): Promise<void> {
  await api.post(`/timesheets/${timesheetId}/sign-company`, { image_data_url: imageDataUrl });
}

export interface SignQrResponse {
  url?: string;
  qr_data_uri?: string;
  expires_at?: string | null;
  ios_app_signed_in?: boolean;
}

export async function getCompanySignQr(timesheetId: number): Promise<SignQrResponse> {
  try {
    return await api.get<SignQrResponse>(`/timesheets/${timesheetId}/company-sign-qr`);
  } catch {
    return api.get<SignQrResponse>(`/timesheets/${timesheetId}/sign-qr`);
  }
}

export async function getPublicLinks(timesheetId: number): Promise<{
  draft_url?: string;
  signed_url?: string;
  [key: string]: unknown;
}> {
  return api.get(`/timesheets/${timesheetId}/public-links`);
}

export interface MailSuggestion {
  recipients?: string[];
  cc?: string[];
  subject?: string | null;
  message?: string | null;
  pdf_url?: string | null;
}

export async function getMailSuggestion(timesheetId: number): Promise<MailSuggestion> {
  const data = await api.get<MailSuggestion & { email?: string | null; cc?: string[] }>(
    `/timesheets/${timesheetId}/mail-suggestion`,
  );
  const email = String(data.email || "").trim();
  const recipients =
    Array.isArray(data.recipients) && data.recipients.length > 0
      ? data.recipients
      : email
        ? [email]
        : [];
  return {
    ...data,
    recipients,
    cc: Array.isArray(data.cc) ? data.cc : [],
    subject: data.subject ?? (recipients.length ? `Stundennachweis` : null),
  };
}

export interface TimesheetReminderMailCompose {
  subject: string;
  html_body: string;
  sent_on?: string | null;
  cc?: string;
}

export async function composeTimesheetReminderMail(
  timesheetId: number,
  recipients?: string,
): Promise<TimesheetReminderMailCompose> {
  const qs = recipients?.trim()
    ? `?recipients=${encodeURIComponent(recipients.trim())}`
    : "";
  return api.get(`/timesheets/${timesheetId}/compose-reminder-mail${qs}`);
}

export async function sendTimesheetMail(
  timesheetId: number,
  body: {
    recipients: string[] | string;
    cc?: string[] | string;
    subject?: string;
    message?: string;
    html_body?: string;
    pdf_url?: string;
    reminder?: boolean;
    mark_contact?: boolean;
  },
): Promise<void> {
  const recipients = Array.isArray(body.recipients) ? body.recipients.join(", ") : body.recipients;
  const cc =
    body.cc == null ? undefined : Array.isArray(body.cc) ? body.cc.join(", ") : body.cc;
  await api.post(`/timesheets/${timesheetId}/send-mail`, { ...body, recipients, cc });
}

export interface PrintLocation {
  id: number;
  name?: string | null;
  [key: string]: unknown;
}

export async function listPrintLocations(): Promise<PrintLocation[]> {
  const data = await api.get<PrintLocation[]>("/print/locations");
  return Array.isArray(data) ? data : [];
}

export interface DefaultPrintLocationResponse {
  default_location_id?: number | null;
}

export async function getDefaultPrintLocation(): Promise<DefaultPrintLocationResponse> {
  const { getDefaultPrintLocation: load } = await import("@/lib/printPreferences");
  return load();
}

export async function setDefaultPrintLocation(
  locationId: number,
): Promise<DefaultPrintLocationResponse> {
  const { setDefaultPrintLocation: save } = await import("@/lib/printPreferences");
  return save(locationId);
}

export {
  printByLocationAndWait,
  queuePrintByLocation,
  runPrintByLocationWithFeedback,
} from "@/lib/printJob";

/** @deprecated Verwende `printByLocationAndWait` oder `runPrintByLocationWithFeedback`. */
export async function printByLocation(
  locationId: number,
  copies: number,
  fileUrl: string,
): Promise<unknown> {
  const { queuePrintByLocation } = await import("@/lib/printJob");
  return queuePrintByLocation(locationId, copies, fileUrl);
}

export async function logTimesheetPrint(
  timesheetId: number,
  body: {
    location_id?: number | string;
    copies?: number;
    printer_name?: string;
    pdf_url?: string;
  },
): Promise<void> {
  await api.post(`/timesheets/${timesheetId}/log/print`, {
    ...body,
    location_id: String(body.location_id),
  });
}

export interface ActionLogEntry {
  id?: number;
  action?: string;
  created_at?: string;
  created_by?: string;
  details?: string | null;
  [key: string]: unknown;
}

export async function getActionLog(timesheetId: number): Promise<ActionLogEntry[]> {
  const data = await api.get<ActionLogEntry[]>(`/timesheets/${timesheetId}/action-log`);
  return Array.isArray(data) ? data : [];
}

export async function fetchLieferscheinHistory(
  auftragId: number,
  docId: number,
): Promise<ActionLogEntry[]> {
  const data = await api.get<ActionLogEntry[]>(
    `/auftraege/${auftragId}/lieferschein/${docId}/history`,
  );
  return Array.isArray(data) ? data : [];
}

export async function createRevision(timesheetId: number): Promise<{ id: number }> {
  return api.post<{ id: number }>(`/timesheets/${timesheetId}/create-revision`, {});
}

export async function archiveTimesheet(timesheetId: number): Promise<void> {
  await api.post(`/timesheets/${timesheetId}/archive`, {});
}

export async function unarchiveTimesheet(timesheetId: number): Promise<void> {
  await api.post(`/timesheets/${timesheetId}/unarchive`, {});
}

export async function assignBilling(timesheetId: number, login: string): Promise<void> {
  await api.post(`/timesheets/${timesheetId}/assign-billing`, { assignee_login: login });
}

export async function markBillingSeen(timesheetId: number): Promise<void> {
  await api.post(`/timesheets/${timesheetId}/mark-billing-seen`, {});
}

export interface BillingQuestion {
  id: number;
  question_text?: string | null;
  response_text?: string | null;
  items?: Array<{
    id: number;
    material_position_id?: number | null;
    question_text?: string;
    response_text?: string;
    price?: string | number | null;
    material_text?: string | null;
    material_menge?: string | number | null;
    material_einheit?: string | null;
    responded_at?: string | null;
  }>;
  [key: string]: unknown;
}

export async function listBillingQuestions(
  timesheetId: number,
  options?: { includeAnswered?: boolean },
): Promise<BillingQuestion[]> {
  const qs = options?.includeAnswered ? "?include_answered=true" : "";
  const data = await api.get<unknown>(`/timesheets/${timesheetId}/billing-questions${qs}`);
  return normalizeBillingQuestionsFromApi(data);
}

export async function createBillingQuestions(
  timesheetId: number,
  payload: {
    question_text?: string | null;
    items?: Array<{ material_position_id?: number | null; question_text?: string | null }>;
  },
): Promise<BillingQuestion | null> {
  const data = await api.post<unknown>(`/timesheets/${timesheetId}/billing-questions`, payload);
  const normalized = normalizeBillingQuestionsFromApi(Array.isArray(data) ? data : data ? [data] : []);
  return normalized[0] ?? null;
}

/** @deprecated Nutze createBillingQuestions */
export async function createBillingQuestion(
  timesheetId: number,
  text: string,
): Promise<void> {
  await createBillingQuestions(timesheetId, { question_text: text });
}

export async function respondBillingQuestion(
  timesheetId: number,
  questionId: number,
  payload: {
    response_text?: string | null;
    items?: Array<{ item_id: number; response_text?: string | null; price?: string | number | null }>;
  },
): Promise<void> {
  await api.post(`/timesheets/${timesheetId}/billing-questions/${questionId}/respond`, payload);
}

export async function appendTimesheetMaterialPositions(
  timesheetId: number,
  materials: TimesheetMaterialRecord[],
): Promise<{ ids: number[] }> {
  const data = await api.post<{ ids?: number[] }>(
    `/timesheets/${timesheetId}/material-positions`,
    materials.map(normalizeMaterialForApi),
  );
  return { ids: Array.isArray(data?.ids) ? data.ids : [] };
}

export async function deleteTimesheetMaterialPositions(
  timesheetId: number,
  ids: number[],
): Promise<{ ids: number[] }> {
  const clean = ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
  if (!clean.length) return { ids: [] };
  const data = await api.del<{ ids?: number[] }>(`/timesheets/${timesheetId}/material-positions`, {
    json: { ids: clean },
  });
  return { ids: Array.isArray(data?.ids) ? data.ids : [] };
}

export async function listHeroProjectInvoices(timesheetId: number): Promise<unknown[]> {
  const data = await api.get<unknown[]>(`/timesheets/${timesheetId}/hero/project-invoices`);
  return Array.isArray(data) ? data : [];
}

export async function createHeroInvoiceDraft(timesheetId: number): Promise<{
  hero_document_url?: string;
  customer_document_id?: number | string;
  invoice_customer_document_id?: number | string;
  invoice_document_id?: number | string;
  [key: string]: unknown;
}> {
  return api.post(`/timesheets/${timesheetId}/hero/invoice-draft`, {});
}

export interface GdInvoiceCandidate {
  id: number;
  display_number?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  total_hours?: number | null;
  work_description?: string | null;
  status?: string | null;
}

export async function listGdInvoiceCandidates(timesheetId: number): Promise<{
  primary: GdInvoiceCandidate;
  candidates: GdInvoiceCandidate[];
  has_additional: boolean;
}> {
  return api.get(`/timesheets/${timesheetId}/dokumente/invoice-candidates`);
}

export async function createGdInvoiceDraft(
  timesheetId: number,
  body?: { timesheet_ids?: number[] },
): Promise<{
  ok?: boolean;
  gd_rechnung_id?: number;
  already_exists?: boolean;
  timesheet_ids?: number[];
}> {
  return api.post(`/timesheets/${timesheetId}/dokumente/invoice-draft`, body ?? {});
}

export interface GdProjectInvoiceOption {
  id: number;
  display_number?: string | null;
  status?: string | null;
  status_label?: string | null;
  belegdatum?: string | null;
  summe_brutto?: number | null;
  rechnung_art?: string | null;
  betreff?: string | null;
}

export async function listGdProjectInvoices(
  timesheetId: number,
): Promise<GdProjectInvoiceOption[]> {
  const data = await api.get<GdProjectInvoiceOption[] | { items?: GdProjectInvoiceOption[] }>(
    `/timesheets/${timesheetId}/dokumente/project-invoices`,
  );
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.items) ? data.items : [];
}

export async function linkGdRechnung(
  timesheetId: number,
  gdRechnungId: number,
): Promise<{
  ok?: boolean;
  gd_rechnung_id?: number;
  already_linked?: boolean;
  timesheet_status?: string;
  display_number?: string | null;
  previous_gd_rechnung_id?: number | null;
}> {
  return api.post(`/timesheets/${timesheetId}/dokumente/link-rechnung`, {
    gd_rechnung_id: gdRechnungId,
  });
}

export async function linkHeroRechnung(
  timesheetId: number,
  body: { invoice_document_id: string | number; invoice_customer_document_id?: string | number },
): Promise<{
  ok?: boolean;
  already_linked?: boolean;
  provider?: string;
  invoice_document_id?: string | number;
  invoice_customer_document_id?: string | number;
  invoice_number?: string | null;
  timesheet_status?: string;
  display_number?: string | null;
}> {
  return api.post(`/timesheets/${timesheetId}/hero/link-invoice`, body);
}

export function gdProjectInvoiceOptionLabel(inv: GdProjectInvoiceOption): string {
  const number = (inv.display_number || "").trim() || `#${inv.id}`;
  const status = (inv.status_label || inv.status || "").trim();
  const date = (inv.belegdatum || "").trim();
  const parts = [number];
  if (status) parts.push(status);
  if (date) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
    parts.push(m ? `${m[3]}.${m[2]}.${m[1]}` : date);
  }
  return parts.join(" · ");
}

export async function reassignHeroInvoice(
  timesheetId: number,
  body: { invoice_document_id: string | number; invoice_customer_document_id?: string | number },
): Promise<unknown> {
  return api.post(`/timesheets/${timesheetId}/hero/reassign-invoice`, body);
}

export interface HeroProjectInvoiceOption {
  id?: number | string;
  customer_document_id?: number | string;
  hero_document_id?: number | string;
  document_id?: number | string;
  number?: string;
  invoice_number?: string;
  display_number?: string;
  title?: string;
  name?: string;
  label?: string;
  description?: string;
  [key: string]: unknown;
}

export function heroInvoiceOptionLabel(inv: HeroProjectInvoiceOption): string {
  const customerDocId = inv.customer_document_id ?? inv.id ?? inv.document_id;
  const heroDocId = inv.hero_document_id ?? inv.document_id ?? inv.hero_id ?? inv.id;
  const number = inv.number ?? inv.invoice_number ?? inv.display_number ?? inv.nr;
  const title = inv.title ?? inv.name ?? inv.label ?? inv.description;
  const parts = [number, title]
    .map((part) => (part == null ? "" : String(part).trim()))
    .filter(Boolean)
    .filter((part, index, all) => all.indexOf(part) === index);
  return parts.length ? parts.join(" · ") : `Rechnung ${customerDocId ?? heroDocId ?? ""}`;
}

export const PORTAL_URL =
  typeof window !== "undefined" && window.location.hostname.includes("dev.")
    ? "https://dev.portal.ernsthaus.de"
    : "https://portal.ernsthaus.de";

export function normalizePortalUrl(url: string): string {
  return url.replace(/^https?:\/\/[^/]+/i, PORTAL_URL);
}
